import { REQUESTING_CLIENT } from '../constants/api';
import type { UserStatus } from '../models/rental-application';
import { getHasNonExpiredReports } from '../utils/reports/report-utils';
import { useApplicationContext } from '../contexts/ApplicationContext';
import {
  useCappsScreeningReportsWebApiHandler_createReports,
  useCappsScreeningReportsWebApiHandler_retrieveReports,
} from '@zg-rentals/ts-capps-client-react-query';
import { getIsReauthenticating } from '../utils/user-statuses';
import { useQueryClient } from '@zg-rentals/react-query5';
import { invalidateRentalApplication } from './useRentalApplication';
import { useCallback } from 'react';

const SCREENING_REPORTS_TAG = 'screeningReports';

// Depending on the application type the api will use listingAlias or zipCode
export function useGetReportsForApplication({ enabled = true }: { enabled?: boolean } = {}) {
  const { application, userStatuses } = useApplicationContext();
  const { rentalListing, applicationShareInformation } = application;
  const reportsEnabled = enabled && getHasNonExpiredReports(userStatuses) && !getIsReauthenticating(userStatuses);

  const retrieveReportParams = rentalListing?.listingAlias
    ? { listingAlias: rentalListing?.listingAlias }
    : { zipCode: applicationShareInformation?.propertyAddress?.zip as string };

  const query = useCappsScreeningReportsWebApiHandler_retrieveReports(
    {
      ...retrieveReportParams,
      requestingClient: REQUESTING_CLIENT,
    },
    { enabled: reportsEnabled, meta: { tags: [SCREENING_REPORTS_TAG] } },
  );

  return {
    ...query,
    isPending: reportsEnabled && query.isPending,
  };
}

export function useCreateReportsForApplication() {
  const { userStatuses } = useApplicationContext();
  const queryClient = useQueryClient();
  const { mutateAsync, isError } = useCappsScreeningReportsWebApiHandler_createReports({
    meta: {
      context: { api: 'cappsScreeningReports', details: 'createReports' },
      invalidates: [SCREENING_REPORTS_TAG],
    },
  });

  const createReports = useCallback(
    async (latestUserStatuses = userStatuses) => {
      if (getIsReauthenticating(latestUserStatuses)) {
        return false;
      }

      await mutateAsync({
        postData: {
          requestingClient: REQUESTING_CLIENT,
        },
      });
      await invalidateRentalApplication(queryClient);
      return true;
    },
    [queryClient, mutateAsync, userStatuses],
  );

  return {
    createReports,
    isError,
  };
}

export function useGetReportsByZip({ zip, userStatuses }: { zip: string; userStatuses: Array<UserStatus> }) {
  return useCappsScreeningReportsWebApiHandler_retrieveReports(
    {
      requestingClient: REQUESTING_CLIENT,
      zipCode: zip,
    },
    { enabled: getHasNonExpiredReports(userStatuses), meta: { tags: [SCREENING_REPORTS_TAG] } },
  );
}
